import React, { useRef, useState, TouchEvent, useContext } from "react";

import { Language, TText } from "api/type";
import { LanguageContext } from "contexts";

interface SliderProps {
  afterImageUrls: string[];
  values: number[];
}

const text: TText = {
  before: {
    en: "Before",
    es: "Antes",
  },
  after: {
    en: "After",
    es: "Después",
  },
};

const ImageSlider: React.FC<SliderProps> = ({
  afterImageUrls,
  values,
}: SliderProps): JSX.Element => {
  const language = useContext<Language>(LanguageContext)[0];
  const ppContainerRef = useRef<HTMLDivElement>(null);
  const [sliderPosition, setSliderPosition] = useState<number>(0.0);
  const [transitionStyles, setTransitionStyles] = useState({
    sliderLine: "",
    afterImage: "",
  });

  const slide = (xPosition: number): void => {
    if (ppContainerRef.current) {
      const containerBoundingRect =
        ppContainerRef.current.getBoundingClientRect();
      setSliderPosition(() => {
        if (xPosition < containerBoundingRect.left) {
          return 0;
        }
        if (xPosition > containerBoundingRect.right) {
          return 1;
        }
        return (
          (xPosition - containerBoundingRect.left) / containerBoundingRect.width
        );
      });
    }
  };

  const handleMouseDown = (): void => {
    window.onmousemove = handleMouseMove;
    window.onmouseup = handleMouseUp;
  };

  const handleMouseMove = (e: MouseEvent): void => {
    setTransitionStyles({
      sliderLine: "",
      afterImage: "",
    });
    slide(e.clientX);
  };

  const handleMouseUp = (): void => {
    window.onmousemove = null;
    window.onmouseup = null;
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
    setTransitionStyles({
      sliderLine: "",
      afterImage: "",
    });
    slide(e.touches.item(0).clientX);
  };

  const handleRelease = () => {
    setTransitionStyles({
      sliderLine: "left 1s ease",
      afterImage: "clip-path 1s ease",
    });
    setSliderPosition(sliderPosition > 0.5 ? 1 : 0);
  };

  if (!language) return <></>;

  const styles = {
    slider: {
      display: "flex",
      position: "relative" as const,
      backgroundColor: "transparent",
      borderRadius: "8px",
      overflow: "hidden",
      width: "100%",
      height: "auto",
    },
    firstImage: {
      position: "relative" as const,
      width: "100%",
      height: "auto",
    },
    image: {
      width: "100%",
      height: "auto",
      objectFit: "contain" as const,
      zIndex: 1,
      position: "absolute" as const,
      inset: 0,
    },
    sliderLine: {
      position: "absolute" as const,
      top: 0,
      height: "100%",
      width: "4px",
      background: "#ffffff",
      cursor: "ew-resize",
      touchAction: "none" as const,
      zIndex: 2,
      left: `calc(${sliderPosition * 100}% - 2px)`,
      transition: transitionStyles.sliderLine,
    },
    sliderIcon: {
      position: "absolute" as const,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "600%",
      height: "auto",
      cursor: "ew-resize",
    },
    text: {
      position: "absolute" as const,
      color: "#fff",
      transition: "opacity 1s ease",
      zIndex: 3,
      fontSize: "16px",
      fontWeight: 500,
      left: "50%",
      top: "20px",
      transform: "translate(-50%, -50%)",
    },
    input: {
      cursor: "ew-resize",
      position: "absolute" as const,
      top: 0,
      left: 0,
      inset: 0,
      zIndex: 1,
      opacity: 0,
    },
  };

  return (
    <div ref={ppContainerRef} style={styles.slider}>
      <p
        style={{
          ...styles.text,
          opacity: sliderPosition <= 0.5 ? 0 : 1,
        }}
      >
        {text.before[language]}
      </p>
      <p
        style={{
          ...styles.text,
          opacity: sliderPosition > 0.5 ? 0 : 1,
        }}
      >
        {text.after[language]}
      </p>
      <img
        style={styles.firstImage}
        draggable={false}
        src={afterImageUrls[0]}
        alt=""
      />
      {afterImageUrls.map((afterImageUrl, index) => (
        <img
          style={{
            ...styles.image,
            opacity: values[0] === index ? 1 : 0,
            clipPath: `polygon(${sliderPosition * 100}% 0, 100% 0, 100% 100%, ${
              sliderPosition * 100
            }% 100%)`,
            transition: transitionStyles.afterImage,
          }}
          draggable={false}
          key={index}
          src={afterImageUrl}
          alt=""
        />
      ))}
      <div
        style={styles.sliderLine}
        onMouseDown={handleMouseDown}
        onTouchMove={handleTouchMove}
        onMouseUp={handleRelease}
        onTouchEnd={handleRelease}
      >
        <div style={styles.sliderIcon}>
          {/* TODO: Move the asset to the asset folder */}
          <svg
            width="23"
            height="23"
            viewBox="0 0 23 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21.8929 11.5C21.8929 17.0689 17.3784 21.5833 11.8096 21.5833C6.2407 21.5833 1.72624 17.0689 1.72624 11.5C1.72624 5.93113 6.2407 1.41667 11.8096 1.41667C17.3784 1.41667 21.8929 5.93113 21.8929 11.5Z"
              stroke="white"
              strokeWidth="1.83333"
            />
            <path
              d="M18.6647 11.1355L16.2713 9.31114C15.9697 9.08119 15.5352 9.29633 15.5352 9.67566V13.3243C15.5352 13.7037 15.9697 13.9188 16.2713 13.6889L18.6647 11.8645C18.9054 11.6811 18.9054 11.3189 18.6647 11.1355Z"
              fill="white"
            />
            <path
              d="M4.9544 11.1355L7.3478 9.31114C7.64949 9.08119 8.08398 9.29633 8.08398 9.67566V13.3243C8.08398 13.7037 7.64949 13.9188 7.3478 13.6889L4.9544 11.8645C4.71375 11.6811 4.71375 11.3189 4.9544 11.1355Z"
              fill="white"
            />
          </svg>
        </div>
      </div>
      <input
        style={styles.input}
        type="range"
        min={0}
        max={1}
        onMouseDown={handleMouseDown}
        onTouchMove={handleTouchMove}
        onMouseUp={handleRelease}
        onTouchEnd={handleRelease}
      />
    </div>
  );
};

export default ImageSlider;
